import { BffResponse } from "../backend-types/common";
import { randomHexString, randomUUID } from "../utils";

const ONE_MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;

export function makeTestBffData(): BffResponse {
  const mockNow = new Date().toISOString();
  const mockExpiry = new Date(Date.now() + ONE_MONTH_IN_MS).toISOString();
  return {
    session: {
      payment_session_id: `ps-${randomHexString(24)}`,
      created: mockNow,
      updated: mockNow,
      status: "ACTIVE",
      reference_id: randomUUID(),
      description: "Test session",
      currency: "IDR",
      amount: 10000,
      country: "ID",
      expires_at: mockExpiry,
      session_type: "PAY",
      mode: "COMPONENTS",
      locale: "en",
      business_id: randomHexString(24),
      customer_id: `cust-${randomUUID()}`,
      capture_method: "AUTOMATIC",
      allow_save_payment_method: "OPTIONAL",
      items: [
        {
          reference_id: randomUUID(),
          type: "DIGITAL_PRODUCT",
          name: "Item 1",
          net_unit_amount: 50000,
          quantity: 1,
          category: "",
        },
        {
          reference_id: randomUUID(),
          type: "DIGITAL_SERVICE",
          name: "Item 2",
          net_unit_amount: 50000,
          quantity: 1,
          category: "",
        },
      ],
    },
    business: {
      name: "Components Mock Merchant",
      country_of_operation: "Indonesia",
      merchant_profile_picture_url:
        "https://placehold.co/256x256.png?text=Logo",
    },
    customer: {
      type: "INDIVIDUAL",
      id: `cust-${randomUUID()}`,
      email: "exa**@example.com",
      mobile_number: null,
      phone_number: null,
      individual_detail: {
        given_names: "exa********",
        surname: null,
      },
      business_detail: null,
    },
    channels: [
      {
        brand_name: "Cards",
        channel_code: "CARDS",
        brand_logo_url:
          "https://assets.xendit.co/payment-session/logos/CARDS.svg",
        ui_group: "cards",
        pm_type: "CARDS",
        allow_pay_without_save: true,
        allow_save: true,
        brand_color: "#000000",
        min_amount: 5000,
        max_amount: 200000000,
        requires_customer_details: false,
        _mock_action_type: "IFRAME",
        card: {
          brands: [
            {
              name: "VISA",
              logo_url:
                "https://assets.xendit.co/payment-session/logos/VISA.svg",
            },
            {
              name: "MASTERCARD",
              logo_url:
                "https://assets.xendit.co/payment-session/logos/MASTERCARD.svg",
            },
            {
              name: "JCB",
              logo_url:
                "https://assets.xendit.co/payment-session/logos/JCB.svg",
            },
          ],
        },
        form: [
          {
            group_label: "Card Details",
            label: "Card Number",
            placeholder: "1234 1234 1234 1234",
            type: {
              name: "credit_card_number",
            },
            channel_property: "card_details.card_number",
            required: true,
            span: 2,
          },
          {
            label: "Card Expiry Date",
            placeholder: "MM/YY",
            type: {
              name: "credit_card_expiry",
            },
            channel_property: [
              "card_details.expiry_month",
              "card_details.expiry_year",
            ],
            required: true,
            span: 1,
            join: true,
          },
          {
            label: "CVN",
            placeholder: "CVV",
            type: {
              name: "credit_card_cvn",
            },
            channel_property: "card_details.cvn",
            required: true,
            span: 1,
            join: true,
          },
          {
            group_label: "Cardholder Name",
            label: "First Name",
            placeholder: "First Name",
            type: {
              name: "text",
              min_length: 1,
              max_length: 50,
              regex_validators: [
                {
                  regex: "/^[a-zA-Z\\u00C0-\\u017F\\s]+$/",
                  message:
                    "Invalid input. Please use alphabetic characters only",
                },
              ],
            },
            channel_property: "card_details.cardholder_first_name",
            required: true,
            span: 1,
          },
          {
            label: "Last Name",
            placeholder: "Last Name",
            type: {
              name: "text",
              min_length: 1,
              max_length: 50,
              regex_validators: [
                {
                  regex: "/^[ -~]+$/",
                  message:
                    "Invalid input. Please use alphabetic characters only",
                },
              ],
            },
            channel_property: "card_details.cardholder_last_name",
            required: true,
            span: 1,
          },
          {
            label: "Email",
            placeholder: "john.doe@example.com",
            type: {
              name: "email",
            },
            channel_property: "card_details.cardholder_email",
            required: true,
            span: 2,
          },
          {
            label: "Mobile Number",
            placeholder: "8000032341",
            type: {
              name: "phone_number",
            },
            channel_property: "card_details.cardholder_phone_number",
            required: true,
            span: 2,
          },
          {
            label: "Installment plan",
            placeholder: "Select installment plan",
            type: {
              name: "installment_plan",
            },
            channel_property: [
              "installment_configuration.terms",
              "installment_configuration.interval",
              "installment_configuration.code",
            ],
            required: false,
            span: 2,
          },
          {
            group_label: "Billing Address",
            label: "First Name",
            placeholder: "First Name",
            type: {
              name: "text",
              max_length: 255,
            },
            channel_property: "billing_information.first_name",
            span: 1,
            required: true,
            flags: {
              require_billing_information: true,
            },
          },
          {
            label: "Last Name",
            placeholder: "Last Name",
            type: {
              name: "text",
              max_length: 255,
            },
            channel_property: "billing_information.last_name",
            span: 1,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "Email",
            placeholder: "Email",
            type: {
              name: "text",
              max_length: 255,
            },
            channel_property: "billing_information.email",
            span: 2,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "Country",
            placeholder: "Country",
            type: {
              name: "country",
            },
            channel_property: "billing_information.country",
            span: 1,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "Province",
            placeholder: "State / Province",
            type: {
              name: "province",
            },
            channel_property: "billing_information.province_state",
            span: 1,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "City",
            placeholder: "City",
            type: {
              name: "text",
              max_length: 255,
            },
            channel_property: "billing_information.city",
            span: 2,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "Address Line 1",
            placeholder: "Address Line 1",
            type: {
              name: "text",
              max_length: 255,
            },
            channel_property: "billing_information.street_line1",
            span: 2,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "Address Line 2",
            placeholder: "Address Line 2",
            type: {
              name: "text",
              max_length: 255,
            },
            channel_property: "billing_information.street_line2",
            span: 2,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
          {
            label: "Postal Code",
            placeholder: "Postal Code",
            type: {
              name: "postal_code",
            },
            channel_property: "billing_information.postal_code",
            span: 2,
            required: true,
            flags: {
              require_billing_information: true,
            },
            join: true,
          },
        ],
        instructions: [
          "Mock Cards channel",
          "This mock channel behaves similarly to the production CARDS channel.",
        ],
      },
      {
        brand_name: "Mock E-Wallet Channel (Redirect action)",
        channel_code: "MOCK_EWALLET",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "EWALLET",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "REDIRECT",
        form: [],
        instructions: [
          "Mock E-Wallet channel",
          "This mock channel has a redirect action.",
        ],
      },
      {
        brand_name: "Mock E-Wallet Channel (Redirect and Paylink actions)",
        channel_code: "MOCK_EWALLET_PAYLINK",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "EWALLET",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: ["REDIRECT", "PAYLINK"],
        form: [],
        instructions: [
          "Mock E-Wallet channel",
          "This mock channel has a redirect and a paylink action.",
        ],
      },
      {
        brand_name: "Mock E-Wallet Channel (Iframe action)",
        channel_code: "MOCK_EWALLET_IFRAME",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "EWALLET",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "IFRAME",
        form: [],
        instructions: [
          "Mock E-Wallet channel",
          "This mock channel has as iframe action.",
        ],
      },
      {
        brand_name: "Mock E-Wallet Channel (Deeplink action)",
        channel_code: "MOCK_EWALLET_DEEP_LINK",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "EWALLET",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "DEEP_LINK",
        form: [],
        instructions: [
          "Mock E-Wallet channel",
          "This mock channel has a deeplink action.",
        ],
      },
      {
        brand_name: "Mock E-Wallet Channel (Push notification action)",
        channel_code: "MOCK_EWALLET_PUSH_NOTIFICATION",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "EWALLET",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "PUSH_NOTIFICATION",
        form: [],
        instructions: [
          "Mock E-Wallet channel",
          "This mock channel shows a message prompting the user to tap a push notification.",
        ],
      },
      {
        brand_name: "Mock E-Wallet Channel (Phone input)",
        channel_code: "MOCK_EWALLET_WITH_PHONE",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "EWALLET",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "REDIRECT",
        form: [
          {
            label: "Phone Number",
            placeholder: "123 123 132",
            type: {
              name: "phone_number",
            },
            channel_property: "phone_number_field",
            required: true,
            span: 2,
          },
        ],
        instructions: [
          "Mock E-Wallet channel",
          "This mock channel behaves similarly to production E-Wallets channels with a phone number input.",
        ],
      },
      {
        brand_name: "Mock QR Channel",
        channel_code: "MOCK_QR",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "QR_CODE",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "QR",
        form: [],
        instructions: [
          "Mock QR channel",
          "This mock channel behaves similarly to production QR channels.",
        ],
      },
      {
        brand_name: "Mock QR Channel (Custom Art)",
        channel_code: "MOCK_QR_CUSTOM_ART",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "QR_CODE",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "QR",
        form: [
          {
            label: "QR Style",
            placeholder: "Select",
            type: {
              name: "dropdown",
              options: [
                { label: "QRIS", value: "QRIS" },
                { label: "PayNow", value: "SGQR" },
                {
                  label: "Duitnow QR",
                  value: "DUITNOW_QR",
                },
                { label: "PromptPay", value: "PROMPTPAY" },
                { label: "QRPH", value: "QRPH" },
              ],
            },
            channel_property: "mock_channel_code_for_qr_art",
            required: false,
            span: 2,
          },
        ],
        instructions: [
          "Mock QR channel",
          "This mock channel has a QR action with branded layouts. You can choose what branded layout to use.",
        ],
      },
      {
        brand_name: "Mock Direct Debit Channel",
        channel_code: "MOCK_DIRECT_DEBIT",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "DIRECT_DEBIT",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [],
        instructions: [
          "Mock Direct Debit channel",
          "This mock channel behaves similarly to production Direct Debit channels.",
        ],
      },
      {
        brand_name: "Mock FPX Business Channel",
        channel_code: "MOCK_FPX_BUSINESS",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "ONLINE_BANKING",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "PENDING",
        form: [],
        instructions: [
          "Mock FPX channel",
          "This mock channel behaves similarly to production FPX channels. It has a banner and it will enter the pending state after submission.",
        ],
        banner: {
          image_url:
            "https://assets.xendit.co/payment-session/banners/fpx-banner.svg",
          alt_text: "FPX Pay with Online Banking",
          aspect_ratio: 9.7,
        },
      },
      {
        brand_name: "Mock OTC Channel",
        channel_code: "MOCK_OTC",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "OVER_THE_COUNTER",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "BARCODE",
        form: [
          {
            label: "Payer Name",
            placeholder: "Payer Name",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "payer_name",
            required: true,
            span: 2,
          },
        ],
        instructions: [
          "Mock OTC channel",
          "This mock channel behaves similarly to production OTC channels.",
        ],
      },
      {
        brand_name: "Mock VA Channel",
        channel_code: "MOCK_VA",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "VIRTUAL_ACCOUNT",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        _mock_action_type: "VA",
        form: [],
        instructions: [
          "Mock VA channel",
          "This mock channel behaves similarly to production VA channels.",
        ],
      },
      {
        brand_name: "Mock Installments Channel",
        channel_code: "MOCK_INSTALLMENTS",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "other",
        pm_type: "CARDS_INSTALLMENT",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "Installment plan",
            placeholder: "Select installment plan",
            type: {
              name: "installment_plan",
            },
            channel_property: [
              "installment_configuration.terms",
              "installment_configuration.interval",
              "installment_configuration.code",
            ],
            required: false,
            span: 2,
          },
        ],
        instructions: [
          "Mock Installments channel",
          "This mock channel behaves similarly to production installment channels in Thailand.",
        ],
      },
      {
        brand_name: "Input Test",
        channel_code: "UI_INPUT_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "Card Number (Iframe)",
            placeholder: "1234 1234 1234 1234",
            type: {
              name: "credit_card_number",
            },
            channel_property: "card_number",
            required: false,
            span: 2,
          },
          {
            label: "Card Expiry Date (Iframe)",
            placeholder: "MM/YY",
            type: {
              name: "credit_card_expiry",
            },
            channel_property: ["expiry_month", "expiry_year"],
            required: false,
            span: 2,
          },
          {
            label: "CVN (Iframe)",
            placeholder: "CVV",
            type: {
              name: "credit_card_cvn",
            },
            channel_property: "cvn",
            required: false,
            span: 2,
          },
          {
            label: "Text",
            placeholder: "Text",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "text_field",
            required: false,
            span: 2,
          },
          {
            label: "Phone Number",
            placeholder: "123 123 123",
            type: {
              name: "phone_number",
            },
            channel_property: "phone_number_field",
            required: false,
            span: 2,
          },
          {
            label: "Email",
            placeholder: "test@example.com",
            type: {
              name: "email",
            },
            channel_property: "email_field",
            required: false,
            span: 2,
          },
          {
            label: "Postal Code",
            placeholder: "123456",
            type: {
              name: "postal_code",
            },
            channel_property: "postal_code_field",
            required: false,
            span: 2,
          },
          {
            label: "Country",
            placeholder: "Select",
            type: {
              name: "country",
            },
            channel_property: "country_field",
            required: false,
            span: 2,
          },
          {
            label: "Dropdown",
            placeholder: "Select",
            type: {
              name: "dropdown",
              options: [
                { label: "Option 1", value: "option_1" },
                { label: "Option 2", value: "option_2" },
                { label: "Option 3", value: "option_3" },
                {
                  label: "Option 4",
                  value: "option_4",
                  subtitle: "With subtitle",
                },
                {
                  label: "Option 5",
                  value: "option_5",
                  subtitle: "With subtitle",
                },
              ],
            },
            channel_property: "dropdown_field",
            required: false,
            span: 2,
          },
          {
            label: "Dropdown With Icons",
            placeholder: "Select",
            type: {
              name: "dropdown",
              options: [
                {
                  label: "Option 1",
                  value: "option_1",
                  icon_url: "https://placehold.co/16",
                },
                {
                  label: "Option 2",
                  value: "option_2",
                  icon_url: "https://placehold.co/16",
                },
                {
                  label: "Option 3",
                  value: "option_3",
                  icon_url: "https://placehold.co/16",
                },
              ],
            },
            channel_property: "dropdown_field_with_icons",
            required: false,
            span: 2,
          },
        ],
        instructions: [
          "This test demonstrates basic usage of all input types.",
          "All fields here are optional and blank values should pass validation.",
        ],
      },
      {
        brand_name: "Initial Value Test",
        channel_code: "UI_INITIAL_VALUE_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "Text",
            placeholder: "Text",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "text_field",
            required: false,
            span: 2,
            initial_value: "Initial value",
          },
          {
            label: "Phone Number",
            placeholder: "123 123 123",
            type: {
              name: "phone_number",
            },
            channel_property: "phone_number_field",
            required: false,
            span: 2,
            initial_value: "+6581234567",
          },
          {
            label: "Email",
            placeholder: "test@example.com",
            type: {
              name: "email",
            },
            channel_property: "email_field",
            required: false,
            span: 2,
            initial_value: "initial_value@test.com",
          },
          {
            label: "Postal Code",
            placeholder: "123456",
            type: {
              name: "postal_code",
            },
            channel_property: "postal_code_field",
            required: false,
            span: 2,
            initial_value: "123456",
          },
          {
            label: "Country",
            placeholder: "Select",
            type: {
              name: "country",
            },
            channel_property: "country_field",
            required: false,
            span: 2,
            initial_value: "SG",
          },
          {
            label: "Dropdown",
            placeholder: "Select",
            type: {
              name: "dropdown",
              options: [
                { label: "Option 1", value: "option_1" },
                { label: "Option 2", value: "option_2" },
                { label: "Option 3", value: "option_3" },
              ],
            },
            channel_property: "dropdown_field",
            required: false,
            span: 2,
            initial_value: "option_2",
          },
        ],
        instructions: [
          "This test demonstrates initial field values.",
          "All fields here should be populated by default.",
        ],
      },
      {
        brand_name: "Field Grouping Test",
        channel_code: "UI_FIELD_GROUPING_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            group_label: "Vertical Grouping",
            label: "Top",
            placeholder: "Top",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "a1",
            required: false,
            span: 2,
          },
          {
            label: "Middle",
            placeholder: "Middle",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "a2",
            required: false,
            span: 2,
            join: true,
          },
          {
            label: "Bottom",
            placeholder: "Bottom",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "a3",
            required: false,
            span: 2,
            join: true,
          },
          {
            group_label: "Horizontal Grouping",
            label: "Text",
            placeholder: "Left",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "b1",
            required: false,
            span: 1,
          },
          {
            label: "Text",
            placeholder: "Right",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "b1",
            required: false,
            span: 1,
            join: true,
          },
          {
            group_label: "Mixed Grouping",
            label: "Text",
            placeholder: "Top Left",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "c1",
            required: false,
            span: 1,
          },
          {
            label: "Text",
            placeholder: "Top Right",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "c2",
            required: false,
            span: 1,
            join: true,
          },
          {
            label: "Text",
            placeholder: "Middle Left",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "c3",
            required: false,
            span: 1,
            join: true,
          },
          {
            label: "Text",
            placeholder: "Middle Right",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "c4",
            required: false,
            span: 1,
            join: true,
          },
          {
            label: "Text",
            placeholder: "Bottom Left",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "c5",
            required: false,
            span: 1,
            join: true,
          },
          {
            label: "Text",
            placeholder: "Bottom Right",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "c6",
            required: false,
            span: 1,
            join: true,
          },
        ],
        instructions: [
          "This test demonstrates field grouping.",
          "Fields can be 1 or 2 cells wide and can be grouped in any arrangement.",
        ],
      },
      {
        brand_name: "Text Validation Test",
        channel_code: "UI_TEXT_VALIDATION_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "Required Text Field",
            placeholder: "Required",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "required",
            required: true,
            span: 2,
          },
          {
            label: "Validate Minimum Length (5)",
            placeholder: "Min 5 characters",
            type: {
              name: "text",
              min_length: 5,
              max_length: 50,
            },
            channel_property: "min_5_chars",
            required: false,
            span: 2,
          },
          {
            label: "Validate Using Regexp (/^[a-zA-Z0-9]+$/)",
            placeholder: "Alphanumeric only",
            type: {
              name: "text",
              max_length: 50,
              regex_validators: [
                {
                  regex: "/^[a-zA-Z0-9]+$/",
                  message:
                    "Invalid input. Please use alphanumeric characters only",
                },
              ],
            },
            channel_property: "regexp_validation",
            required: false,
            span: 2,
          },
        ],
        instructions: [
          "This test demonstrates text field validation.",
          "Fields should validate on blur or `submit()` or `revealValidationErrors()`. Blurring a blank field does not trigger validation.",
        ],
      },
      {
        brand_name: "State/Province Field Test",
        channel_code: "UI_STATE_PROVINCE_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            group_label: "Country + State/Province Fields",
            label: "Country",
            placeholder: "Country",
            type: {
              name: "country",
            },
            channel_property: "country_field",
            required: true,
            span: 2,
            initial_value: "US",
          },
          {
            label: "State / Province",
            placeholder: "State / Province",
            type: {
              name: "province",
            },
            channel_property: "province_field",
            required: true,
            span: 2,
            join: true,
            initial_value: "CA",
          },
          {
            group_label:
              "Country + State/Province Fields (Horizontal Grouping)",
            label: "Country",
            placeholder: "Country",
            type: {
              name: "country",
            },
            channel_property: "country_field_2",
            required: true,
            span: 1,
            initial_value: "AU",
          },
          {
            label: "State / Province",
            placeholder: "State / Province",
            type: {
              name: "province",
            },
            channel_property: "province_field_2",
            required: true,
            span: 1,
            join: true,
            initial_value: "NSW",
          },
        ],
        instructions: [
          "This test demonstrates the country + state/province field combination.",
          "Selecting certain countries (US, CA, GB) should change the state/province field to a dropdown with predefined options.",
        ],
      },
      {
        brand_name: "Conditional Field Test",
        channel_code: "UI_CONDITIONAL_FIELD_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "Toggle Hidden Field",
            placeholder: "Select",
            type: {
              name: "dropdown",
              options: [
                { label: "Show", value: "show" },
                { label: "Hide", value: "hide" },
              ],
            },
            channel_property: "toggle",
            required: true,
            span: 2,
          },
          {
            label: "Conditional Field",
            placeholder: "This field is conditionally shown",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "conditional_field",
            required: true,
            span: 2,
            join: true,
            display_if: [["toggle", "equals", "show"]],
          },
        ],
        instructions: [
          "This test demonstrates conditional field display based on another field's value.",
          "The second field should only appear if 'Show' is selected in the first field.",
        ],
      },
      {
        brand_name: "Save Payment Details Test",
        channel_code: "UI_SAVE_PAYMENT_DETAILS_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: true,
        allow_save: true,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [],
        instructions: [
          "This channel supports both one-time payment and pay-and-save.",
          "A checkbox should be displayed.",
        ],
      },
      {
        brand_name: "Paired Channels Test",
        channel_code: "UI_PAIRED_CHANNELS_TEST_1",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: true,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "This should show if the save checkbox is unchecked",
            placeholder: "Unchecked",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "nonsave",
            required: false,
            span: 2,
          },
        ],
        instructions: [
          "This channel does not support save, but has a paired channel that does.",
          "Changing the save checkbox switches between the two channels.",
        ],
      },
      {
        brand_name: "Paired Channels Test",
        channel_code: "UI_PAIRED_CHANNELS_TEST_2",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: false,
        allow_save: true,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [
          {
            label: "This should show if the save checkbox is checked",
            placeholder: "Checked",
            type: {
              name: "text",
              max_length: 50,
            },
            channel_property: "save",
            required: false,
            span: 2,
          },
        ],
        instructions: [
          "This channel can only be used for pay-and-save, but has a paired channel that supports one time payment.",
          "Changing the save checkbox switches between the two channels.",
        ],
      },
      {
        brand_name: "Banner Test",
        channel_code: "UI_BANNER_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "ui_tests",
        allow_pay_without_save: true,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        banner: {
          image_url: "https://placehold.co/512x32.svg?text=Banner",
          alt_text: "",
          aspect_ratio: 16,
        },
        form: [],
        instructions: [
          "This channel has a banner image.",
          "It should be displayed above this text.",
        ],
      },
      {
        brand_name: "Single Item Test",
        channel_code: "GROUP_SINGLE_ITEM_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "single_item",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [],
        instructions: [
          "This channel is in a group by itself.",
          "The channel picker should automatically select it when the group is opened.",
        ],
      },
      {
        brand_name: "Disabled Group Test",
        channel_code: "GROUP_DISABLED_GROUP_TEST",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "disabled",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 999999999,
        max_amount: 9999999999,
        requires_customer_details: false,
        form: [],
        instructions: [
          "This channel is in a disabled group.",
          "Since all channels in this group are disabled, the group should not be selectable. You should never see this text because creating this channel will throw an error.",
        ],
      },
      {
        brand_name: "Partial Disabled Group (Enabled)",
        channel_code: "GROUP_PARTIAL_DISABLED_1",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "partial_disabled",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 1000,
        max_amount: 100000000,
        requires_customer_details: false,
        form: [],
        instructions: [
          "This channel is in a partially disabled group.",
          "This channel is enabled, but the other channel in the group is disabled. This channel should be automatically selected by the channel picker because it's the only enabled option.",
        ],
      },
      {
        brand_name: "Partial Disabled Group (Disabled)",
        channel_code: "GROUP_PARTIAL_DISABLED_2",
        brand_logo_url: "https://placehold.co/48x48.png?text=Logo",
        ui_group: "partial_disabled",
        allow_pay_without_save: false,
        allow_save: false,
        brand_color: "#000000",
        min_amount: 999999999,
        max_amount: 9999999999,
        requires_customer_details: false,
        form: [],
        instructions: [
          "This channel is in a partially disabled group.",
          "This channel is disabled. You should never see this text because creating this channel will throw an error.",
        ],
      },
    ],
    channel_ui_groups: [
      {
        id: "cards",
        label: "Mock Cards",
        icon_url: "https://assets.xendit.co/payment-session/logos/CARDS.svg",
      },
      {
        id: "other",
        label: "Other Mock Channels",
        icon_url: "https://placehold.co/48x48.png?text=Logo",
      },
      {
        id: "ui_tests",
        label: "Channel UI Test Cases",
        icon_url: "https://placehold.co/48x48.png?text=Logo",
      },
      {
        id: "single_item",
        label: "Mock Single Item Group",
        icon_url: "https://placehold.co/48x48.png?text=Logo",
      },
      {
        id: "disabled",
        label: "Mock Disabled Group",
        icon_url: "https://placehold.co/48x48.png?text=Logo",
      },
      {
        id: "partial_disabled",
        label: "Mock Partial Disabled Group",
        icon_url: "https://placehold.co/48x48.png?text=Logo",
      },
    ],
    digital_wallets: {
      google_pay: {
        merchant_id: "mock-googlepay-merchant-id",
        allowed_payment_methods: [
          {
            channel_code: "CARDS",
            payment_request_id: `pr-${randomHexString(32)}`,
            payment_method_specification: {
              type: "CARD",
              parameters: {
                billingAddressRequired: true,
                allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                allowedCardNetworks: ["AMEX", "MASTERCARD", "VISA"],
              } satisfies google.payments.api.CardParameters,
              tokenizationSpecification: {
                type: "PAYMENT_GATEWAY",
                parameters: {
                  gateway: "xendit",
                  gatewayMerchantId: "exampleGatewayMerchantId",
                },
              },
            },
          },
        ],
      },
      apple_pay: {
        merchant_id: "platformintegrator.com.xendit",
        apple_pay_payment_request: {
          countryCode: "MY",
          currencyCode: "MYR",
          supportedNetworks: ["visa", "masterCard"],
          merchantCapabilities: ["supports3DS"],
          total: {
            label: "Test session",
            amount: "100",
            type: "final",
          },
          requiredBillingContactFields: [
            "postalAddress",
            "name",
            "email",
            "phone",
          ],
          requiredShippingContactFields: [
            "postalAddress",
            "name",
            "email",
            "phone",
          ],
        },
      },
    },
  };
}
